// CreateSamplingEventForm.js
// This is the form for creating a new sampling event, allowing the user to select the project, phase, and task if needed, along with the sampling site.
// The user will be able to create sampling groups/teams, adding employees to the sampling group/team, and a list of wells for each team.
// This will only be the initial setup form, editing this form can take place in the SamplingEventPage component/form.

import React, { useState, useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// import { useGlobalState } from "../../context/GlobalStateContext";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../store/appStore";
import { useUserState } from "../../context/user";
import styles from "./CreateSamplingEventForm.module.css";
import { samplingEventSelect } from "../samplingEventForm/common/delSelect";

// Selecting the sampling site, react select component:
const SamplingSiteSelect = ({ samplingEventState, handleSiteNameChange }) => {
  return (
    <Select
      options={samplingEventState?.samplingSites}
      value={
        samplingEventState?.newSelectedSamplingSite || {
          value: null,
          label: null,
        }
      }
      onChange={(selectedOption) => {
        handleSiteNameChange(selectedOption);
      }}
      styles={samplingEventSelect(samplingEventState?.formErrors?.samplingSite)}
      placeholder={
        samplingEventState?.formErrors?.samplingSite
          ? samplingEventState.formErrors.samplingSite
          : "Select sampling site"
      }
    />
  );
};

// WellCard component
const WellCard = ({ well, isSelected, onSelect, assignedToGroup }) => {
  // Determine if well is assigned and to which group
  const assignedGroup = assignedToGroup || well.group_name || "Unassigned";
  const isAssigned = assignedGroup !== "Unassigned";

  return (
    <div
      className={`${styles.wellCard} ${
        isSelected ? styles.wellCardSelected : ""
      } ${isAssigned ? styles.wellCardAssigned : ""}`}
      onClick={() => !isAssigned && onSelect(well.well_id)}
    >
      <div className={styles.wellId}>{well.well_id}</div>
      <div className={styles.wellPurpose}>
        Purpose: {well.well_purpose || "N/A"}
      </div>
      <div className={styles.wellStatus}>Status: {well.status || "N/A"}</div>
      <div className={styles.wellInstallDate}>
        Install Date: {well.installation_date || "N/A"}
      </div>
      <div className={styles.wellAssignment}>Assigned To: {assignedGroup}</div>
    </div>
  );
};

// FilterMenu component
const FilterMenu = ({
  wellFilters,
  setWellFilters,
  wellSort,
  setWellSort,
  applyFilters,
  resetFilters,
  closeFilterMenu,
}) => {
  const [tempFilters, setTempFilters] = useState(wellFilters);
  const [tempSort, setTempSort] = useState(wellSort);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSortChange = (e) => {
    const { name, value } = e.target;
    setTempSort((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    setWellFilters(tempFilters);
    setWellSort(tempSort);
    applyFilters(tempFilters, tempSort);
    closeFilterMenu();
  };

  const handleReset = () => {
    const defaultFilters = {
      wellId: "",
      wellPurpose: "",
      status: "",
      groupAssignment: "all",
    };
    const defaultSort = {
      field: "well_id",
      direction: "asc",
    };

    setTempFilters(defaultFilters);
    setTempSort(defaultSort);
    setWellFilters(defaultFilters);
    setWellSort(defaultSort);
    resetFilters();
    closeFilterMenu();
  };

  return (
    <div className={styles.filterMenu}>
      <div className={styles.filterGroup}>
        <label htmlFor="wellId">Well ID:</label>
        <input
          type="text"
          id="wellId"
          name="wellId"
          value={tempFilters.wellId}
          onChange={handleFilterChange}
          className={styles.filterInput}
          placeholder="Filter by Well ID"
        />
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor="wellPurpose">Well Purpose:</label>
        <input
          type="text"
          id="wellPurpose"
          name="wellPurpose"
          value={tempFilters.wellPurpose}
          onChange={handleFilterChange}
          className={styles.filterInput}
          placeholder="Filter by Well Purpose"
        />
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor="status">Status:</label>
        <input
          type="text"
          id="status"
          name="status"
          value={tempFilters.status}
          onChange={handleFilterChange}
          className={styles.filterInput}
          placeholder="Filter by Status"
        />
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor="groupAssignment">Group Assignment:</label>
        <select
          id="groupAssignment"
          name="groupAssignment"
          value={tempFilters.groupAssignment}
          onChange={handleFilterChange}
          className={styles.filterInput}
        >
          <option value="all">All Wells</option>
          <option value="assigned">Assigned Wells</option>
          <option value="unassigned">Unassigned Wells</option>
        </select>
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor="sortField">Sort By:</label>
        <select
          id="sortField"
          name="field"
          value={tempSort.field}
          onChange={handleSortChange}
          className={styles.filterInput}
        >
          <option value="well_id">Well ID</option>
          <option value="well_purpose">Well Purpose</option>
          <option value="status">Status</option>
          <option value="installation_date">Installation Date</option>
        </select>
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor="sortDirection">Sort Direction:</label>
        <select
          id="sortDirection"
          name="direction"
          value={tempSort.direction}
          onChange={handleSortChange}
          className={styles.filterInput}
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>

      <div className={styles.filterActions}>
        <button className={styles.resetFilter} onClick={handleReset}>
          Reset
        </button>
        <button className={styles.applyFilter} onClick={handleApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

// WellsList component
const WellsList = ({ samplingEventState }) => {
  const {
    selectedSiteWellInfo,
    selectedWellIds,
    setSelectedWellIds,
    currentWellPage,
    setCurrentWellPage,
    wellsPerPage,
    setWellsPerPage,
    wellFilters,
    setWellFilters,
    wellSort,
    setWellSort,
    isFilterMenuOpen,
    setIsFilterMenuOpen,
    samplingGroups,
  } = samplingEventState;

  // Create ref for the filter button and menu
  const filterButtonRef = useRef(null);
  const filterMenuRef = useRef(null);

  // Function to get the assigned group name for a well
  const getWellAssignedGroup = (wellId) => {
    for (const group of samplingGroups) {
      const foundWell = group.wells.find((well) => well.well_id === wellId);
      if (foundWell) {
        return group.name;
      }
    }
    return null;
  };

  // Handle click outside to close filter menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle if filter menu is open
      if (!isFilterMenuOpen) return;

      // If click is outside both filter button and filter menu, close the menu
      if (
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target) &&
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setIsFilterMenuOpen(false);
      }
    };

    // Add the event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterMenuOpen, setIsFilterMenuOpen]);

  // Filter and sort the wells
  const filteredAndSortedWells = useMemo(() => {
    if (
      !selectedSiteWellInfo?.wells ||
      selectedSiteWellInfo.wells.length === 0
    ) {
      return [];
    }

    // Apply filters
    let result = [...selectedSiteWellInfo.wells];

    if (wellFilters.wellId) {
      result = result.filter((well) =>
        well.well_id?.toLowerCase().includes(wellFilters.wellId.toLowerCase())
      );
    }

    if (wellFilters.wellPurpose) {
      result = result.filter((well) =>
        well.well_purpose
          ?.toLowerCase()
          .includes(wellFilters.wellPurpose.toLowerCase())
      );
    }

    if (wellFilters.status) {
      result = result.filter((well) =>
        well.status?.toLowerCase().includes(wellFilters.status.toLowerCase())
      );
    }

    // Check if well is assigned to any group for filtering
    if (wellFilters.groupAssignment === "assigned") {
      result = result.filter(
        (well) => getWellAssignedGroup(well.well_id) || well.group_name
      );
    } else if (wellFilters.groupAssignment === "unassigned") {
      result = result.filter(
        (well) => !getWellAssignedGroup(well.well_id) && !well.group_name
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      const aValue = a[wellSort.field] || "";
      const bValue = b[wellSort.field] || "";

      if (wellSort.direction === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return result;
  }, [selectedSiteWellInfo, wellFilters, wellSort, samplingGroups]);

  // Calculate pagination
  const indexOfLastWell = currentWellPage * wellsPerPage;
  const indexOfFirstWell = indexOfLastWell - wellsPerPage;
  const currentWells = filteredAndSortedWells.slice(
    indexOfFirstWell,
    indexOfLastWell
  );
  const totalPages = Math.ceil(filteredAndSortedWells.length / wellsPerPage);

  // Handle well selection
  const handleWellSelect = (wellId) => {
    // Don't select if already assigned to a group
    if (getWellAssignedGroup(wellId)) return;

    setSelectedWellIds((prevSelected) => {
      if (prevSelected.includes(wellId)) {
        return prevSelected.filter((id) => id !== wellId);
      } else {
        return [...prevSelected, wellId];
      }
    });
  };

  // Handle select all on current page
  const handleSelectAllOnPage = () => {
    const unassignedWellIds = currentWells
      .filter((well) => !getWellAssignedGroup(well.well_id))
      .map((well) => well.well_id);

    setSelectedWellIds((prevSelected) => {
      const newSelected = [...prevSelected];

      unassignedWellIds.forEach((wellId) => {
        if (!newSelected.includes(wellId)) {
          newSelected.push(wellId);
        }
      });

      return newSelected;
    });
  };

  // Handle deselect all on current page
  const handleDeselectAllOnPage = () => {
    const currentWellIds = currentWells.map((well) => well.well_id);
    setSelectedWellIds((prevSelected) =>
      prevSelected.filter((wellId) => !currentWellIds.includes(wellId))
    );
  };

  // Handle page change
  const handlePageChange = (direction) => {
    if (direction === "next" && currentWellPage < totalPages) {
      setCurrentWellPage(currentWellPage + 1);
    } else if (direction === "prev" && currentWellPage > 1) {
      setCurrentWellPage(currentWellPage - 1);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setWellsPerPage(newSize);
    setCurrentWellPage(1); // Reset to first page when changing page size
  };

  // Toggle filter menu
  const toggleFilterMenu = () => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };

  // Apply filters
  const applyFilters = (filters, sort) => {
    setWellFilters(filters);
    setWellSort(sort);
    setCurrentWellPage(1); // Reset to first page when filters change
  };

  // Reset filters
  const resetFilters = () => {
    setWellFilters({
      wellId: "",
      wellPurpose: "",
      status: "",
      groupAssignment: "all",
    });
    setWellSort({
      field: "well_id",
      direction: "asc",
    });
    setCurrentWellPage(1); // Reset to first page when filters reset
  };

  if (!selectedSiteWellInfo || !selectedSiteWellInfo.wells) {
    return (
      <div className={styles.noWells}>Select a sampling site to view wells</div>
    );
  }

  if (selectedSiteWellInfo.wells.length === 0) {
    return <div className={styles.noWells}>No wells found for this site</div>;
  }

  return (
    <div className={styles.wellsContainer}>
      <div className={styles.filterControls}>
        <div className={styles.filterButtonContainer}>
          <button
            className={styles.filterButton}
            onClick={toggleFilterMenu}
            ref={filterButtonRef}
          >
            Filter & Sort
          </button>
          {isFilterMenuOpen && (
            <div ref={filterMenuRef}>
              <FilterMenu
                wellFilters={wellFilters}
                setWellFilters={setWellFilters}
                wellSort={wellSort}
                setWellSort={setWellSort}
                applyFilters={applyFilters}
                resetFilters={resetFilters}
                closeFilterMenu={() => setIsFilterMenuOpen(false)}
              />
            </div>
          )}
        </div>
        <div className={styles.selectionControls}>
          <button
            className={styles.selectAllButton}
            onClick={handleSelectAllOnPage}
          >
            Select All on Page
          </button>
          <button
            className={styles.deselectAllButton}
            onClick={handleDeselectAllOnPage}
          >
            Deselect All on Page
          </button>
        </div>
      </div>

      <div className={styles.wellCardsGrid}>
        {currentWells.map((well) => (
          <WellCard
            key={well.well_id}
            well={well}
            isSelected={selectedWellIds.includes(well.well_id)}
            onSelect={handleWellSelect}
            assignedToGroup={getWellAssignedGroup(well.well_id)}
          />
        ))}
      </div>

      <div className={styles.paginationControls}>
        <div className={styles.paginationButtons}>
          <button
            onClick={() => handlePageChange("prev")}
            disabled={currentWellPage === 1}
          >
            Previous
          </button>

          <div className={styles.pageSizeSelector}>
            <label htmlFor="wellsPerPage">Wells per page:</label>
            <select
              id="wellsPerPage"
              value={wellsPerPage}
              onChange={handlePageSizeChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        <div className={styles.paginationInfo}>
          Showing {indexOfFirstWell + 1} -{" "}
          {Math.min(indexOfLastWell, filteredAndSortedWells.length)} of{" "}
          {filteredAndSortedWells.length} wells
        </div>
        <div className={styles.paginationButtons}>
          <button
            onClick={() => handlePageChange("next")}
            disabled={currentWellPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal component for employee selection
const EmployeeSelectionModal = ({
  isOpen,
  onClose,
  onSelectEmployee,
  allEmployees,
  existingEmployees,
  samplingEventState,
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  if (!isOpen) return null;

  // Get all employees already assigned to any group
  const allAssignedEmployees = samplingEventState.samplingGroups.flatMap(
    (group) => group.employees
  );

  // Filter out already assigned employees from all groups
  const availableEmployees = allEmployees.filter(
    (employee) =>
      !allAssignedEmployees.some(
        (existing) => existing.employee_id === employee.employee_id
      )
  );

  const handleSelectEmployee = (selected) => {
    setSelectedEmployees(selected || []);
  };

  const handleAddEmployees = () => {
    if (selectedEmployees.length === 0) return;

    // If single employee was selected (not array)
    if (!Array.isArray(selectedEmployees)) {
      onSelectEmployee([selectedEmployees]);
    } else {
      onSelectEmployee(selectedEmployees);
    }

    setSelectedEmployees([]);
    onClose();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Select Employees</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          <Select
            options={availableEmployees}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.employee_id}
            onChange={handleSelectEmployee}
            value={selectedEmployees}
            placeholder="Search for employees..."
            className={styles.employeeSelect}
            isMulti
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />

          <div className={styles.modalActions}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button
              className={styles.confirmButton}
              onClick={handleAddEmployees}
              disabled={selectedEmployees.length === 0}
            >
              Add Selected Employees
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal component for confirmation
const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Confirm</h3>
          <button className={styles.closeButton} onClick={onCancel}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          <p>{message}</p>
          <div className={styles.modalActions}>
            <button className={styles.cancelButton} onClick={onCancel}>
              Cancel
            </button>
            <button className={styles.confirmButton} onClick={onConfirm}>
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// GroupCard component
const GroupCard = ({
  group,
  samplingEventState,
  allEmployees,
  selectedSiteWellInfo,
}) => {
  const [activeTab, setActiveTab] = useState("employees");
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const {
    samplingGroups,
    setSamplingGroups,
    selectedWellIds,
    setSelectedWellIds,
  } = samplingEventState;

  // Update group name
  const handleNameChange = (e) => {
    const updatedGroups = samplingGroups.map((g) =>
      g.id === group.id ? { ...g, name: e.target.value } : g
    );
    setSamplingGroups(updatedGroups);
  };

  // Add employees to group
  const handleAddEmployees = (employees) => {
    if (!employees || employees.length === 0) return;

    const updatedGroups = samplingGroups.map((g) => {
      if (g.id === group.id) {
        // Filter out any duplicates
        const newEmployees = employees.filter(
          (emp) =>
            !g.employees.some(
              (existing) => existing.employee_id === emp.employee_id
            )
        );

        return {
          ...g,
          employees: [...g.employees, ...newEmployees],
        };
      }
      return g;
    });
    setSamplingGroups(updatedGroups);
  };

  // Remove employee from group
  const handleRemoveEmployee = (employeeId) => {
    const updatedGroups = samplingGroups.map((g) => {
      if (g.id === group.id) {
        return {
          ...g,
          employees: g.employees.filter(
            (emp) => emp.employee_id !== employeeId
          ),
        };
      }
      return g;
    });
    setSamplingGroups(updatedGroups);
  };

  // Add selected wells to group
  const handleAddWells = () => {
    if (selectedWellIds.length === 0) {
      alert("Please select wells to add to this group");
      return;
    }

    // Check if any selected wells are already assigned to other groups
    const assignedWells = [];
    const otherGroups = samplingGroups.filter((g) => g.id !== group.id);

    selectedWellIds.forEach((wellId) => {
      for (const g of otherGroups) {
        if (g.wells.some((w) => w.well_id === wellId)) {
          assignedWells.push(wellId);
          break;
        }
      }
    });

    if (assignedWells.length > 0) {
      alert(
        `The following wells are already assigned to another group: ${assignedWells.join(
          ", "
        )}`
      );
      return;
    }

    // Get well details from selectedSiteWellInfo
    const wellsToAdd = selectedSiteWellInfo.wells
      .filter((well) => selectedWellIds.includes(well.well_id))
      .map((well) => ({
        well_id: well.well_id,
        well_purpose: well.well_purpose,
        status: well.status,
      }));

    // Update groups with added wells
    const updatedGroups = samplingGroups.map((g) => {
      if (g.id === group.id) {
        // Add only wells that aren't already in the group
        const existingWellIds = g.wells.map((w) => w.well_id);
        const newWells = wellsToAdd.filter(
          (well) => !existingWellIds.includes(well.well_id)
        );

        return {
          ...g,
          wells: [...g.wells, ...newWells],
        };
      }
      return g;
    });

    setSamplingGroups(updatedGroups);

    // Clear selected wells
    setSelectedWellIds([]);
  };

  // Remove well from group
  const handleRemoveWell = (wellId) => {
    const updatedGroups = samplingGroups.map((g) => {
      if (g.id === group.id) {
        return {
          ...g,
          wells: g.wells.filter((well) => well.well_id !== wellId),
        };
      }
      return g;
    });
    setSamplingGroups(updatedGroups);
  };

  // Delete group
  const handleDeleteGroup = () => {
    setSamplingGroups(samplingGroups.filter((g) => g.id !== group.id));
    setIsDeleteModalOpen(false);
  };

  return (
    <div className={styles.groupCard}>
      <div className={styles.groupHeader}>
        <input
          type="text"
          value={group.name}
          onChange={handleNameChange}
          className={styles.groupNameInput}
          placeholder="Group Name"
        />
      </div>

      <div className={styles.groupTabs}>
        <button
          className={`${styles.tabButton} ${
            activeTab === "employees" ? styles.activeTab : ""
          }`}
          onClick={() => setActiveTab("employees")}
        >
          Employees
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === "wells" ? styles.activeTab : ""
          }`}
          onClick={() => setActiveTab("wells")}
        >
          Wells
        </button>
      </div>

      <div className={styles.groupTabContent}>
        {activeTab === "employees" && (
          <div className={styles.employeesTab}>
            {group.employees.length > 0 ? (
              <table className={styles.employeeTable}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {group.employees.map((employee) => (
                    <tr key={employee.employee_id}>
                      <td>{employee.label}</td>
                      <td>
                        <button
                          className={styles.removeButton}
                          onClick={() =>
                            handleRemoveEmployee(employee.employee_id)
                          }
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.noData}>No employees assigned</div>
            )}

            <EmployeeSelectionModal
              isOpen={isEmployeeModalOpen}
              onClose={() => setIsEmployeeModalOpen(false)}
              onSelectEmployee={handleAddEmployees}
              allEmployees={allEmployees}
              existingEmployees={group.employees}
              samplingEventState={samplingEventState}
            />
          </div>
        )}

        {activeTab === "wells" && (
          <div className={styles.wellsTab}>
            {group.wells.length > 0 ? (
              <table className={styles.wellsTable}>
                <thead>
                  <tr>
                    <th>Well ID</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {group.wells.map((well) => (
                    <tr key={well.well_id}>
                      <td>{well.well_id}</td>
                      <td>
                        <button
                          className={styles.removeButton}
                          onClick={() => handleRemoveWell(well.well_id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.noData}>No wells assigned</div>
            )}
          </div>
        )}
      </div>

      <div className={styles.groupFooter}>
        {activeTab === "employees" && (
          <button
            className={styles.addButton}
            onClick={() => setIsEmployeeModalOpen(true)}
          >
            Add Employee
          </button>
        )}
        {activeTab === "wells" && (
          <button className={styles.addButton} onClick={handleAddWells}>
            Add Selected Wells
          </button>
        )}
        <button
          className={styles.deleteGroupButton}
          onClick={() => setIsDeleteModalOpen(true)}
        >
          Delete Group
        </button>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        message={`Are you sure you want to delete the group "${group.name}"?`}
        onConfirm={handleDeleteGroup}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

// GroupCreation component
const GroupCreation = ({
  samplingEventState,
  allEmployees,
  selectedSiteWellInfo,
}) => {
  const { samplingGroups, setSamplingGroups, nextGroupId, setNextGroupId } =
    samplingEventState;

  const handleAddGroup = () => {
    const newGroup = {
      id: nextGroupId,
      name: `SG-${nextGroupId}`,
      employees: [],
      wells: [],
    };

    setSamplingGroups([...samplingGroups, newGroup]);
    setNextGroupId(nextGroupId + 1);
  };

  return (
    <div className={styles.groupCreationContainer}>
      <div className={styles.groupsHeader}>
        <button
          type="button"
          className={styles.addGroupButton}
          onClick={handleAddGroup}
        >
          Add Group
        </button>
      </div>

      <div className={styles.groupCardsGrid}>
        {samplingGroups.map((group) => (
          <GroupCard
            key={group.id}
            group={group}
            samplingEventState={samplingEventState}
            allEmployees={allEmployees}
            selectedSiteWellInfo={selectedSiteWellInfo}
          />
        ))}
      </div>
    </div>
  );
};

// Custom date picker container for styling
const customDatePicker = ({ className, children }) => {
  return (
    <div className={className}>
      <div className={styles.calendar}>{children}</div>
    </div>
  );
};

// Event Information section
const EventInformationSection = ({
  samplingEventState,
  allEmployees,
  samplingEventUtils,
}) => {
  const { eventName, setEventName, startDate, endDate, teamLead, formErrors } =
    samplingEventState;

  const handleEventNameChange = (e) => {
    setEventName(e.target.value);
  };

  const handleDateChange = (date, dateType) => {
    samplingEventUtils.handleDateChange(date, dateType);
  };

  const handleTeamLeadSelect = (selected) => {
    samplingEventUtils.handleTeamLeadSelect(selected);
  };

  return (
    <div className={styles.eventInfoSection}>
      <div className={styles.formField}>
        <label htmlFor="eventName">Event Name</label>
        <input
          type="text"
          id="eventName"
          value={eventName}
          onChange={handleEventNameChange}
          className={`${styles.formInput} ${
            formErrors?.eventName ? styles.inputError : ""
          }`}
          placeholder={
            formErrors?.eventName ? formErrors.eventName : "Enter event name"
          }
        />
      </div>

      <div className={styles.formField}>
        <label htmlFor="teamLead">Team Lead</label>
        <Select
          id="teamLead"
          value={teamLead}
          onChange={handleTeamLeadSelect}
          options={allEmployees.map((employee) => ({
            value: employee.employee_id,
            label: employee.label,
          }))}
          placeholder={
            formErrors?.teamLead ? formErrors.teamLead : "Select team lead"
          }
          styles={samplingEventSelect(formErrors?.teamLead)}
          className={formErrors?.teamLead ? styles.error : ""}
        />
      </div>

      <div className={styles.formField}>
        <label htmlFor="startDate">Start Date</label>
        <DatePicker
          id="startDate"
          selected={startDate}
          onChange={(date) => handleDateChange(date, "start")}
          dateFormat="MM/dd/yyyy"
          className={`${styles.dateInput} ${
            formErrors?.startDate || formErrors?.dateRange
              ? styles.inputError
              : ""
          }`}
          placeholderText={
            formErrors?.startDate ? formErrors.startDate : "Select start date"
          }
          isClearable={true}
          showPopperArrow={false}
          calendarContainer={customDatePicker}
          withPortal
          portalId="start-date-picker-portal"
          onFocus={(e) => (e.target.readOnly = true)}
          wrapperClassName={styles.datePickerWrapper}
          showMonthDropdown
          showYearDropdown
          scrollableYearDropdown
        />
      </div>

      <div className={styles.formField}>
        <label htmlFor="endDate">End Date</label>
        <DatePicker
          id="endDate"
          selected={endDate}
          onChange={(date) => handleDateChange(date, "end")}
          dateFormat="MM/dd/yyyy"
          minDate={startDate}
          className={`${styles.dateInput} ${
            formErrors?.endDate || formErrors?.dateRange
              ? styles.inputError
              : ""
          }`}
          placeholderText={
            formErrors?.endDate
              ? formErrors.endDate
              : formErrors?.dateRange
              ? formErrors.dateRange
              : "Select end date"
          }
          isClearable={true}
          showPopperArrow={false}
          calendarContainer={customDatePicker}
          withPortal
          portalId="end-date-picker-portal"
          onFocus={(e) => (e.target.readOnly = true)}
          wrapperClassName={styles.datePickerWrapper}
          showMonthDropdown
          showYearDropdown
          scrollableYearDropdown
        />
      </div>
    </div>
  );
};

// ProjectInformationSection component
const ProjectInformationSection = ({
  samplingEventState,
  allProjectInfo,
  samplingEventUtils,
}) => {
  const {
    noProjectNumberChecked,
    selectedProject,
    selectedPhase,
    selectedTask,
    projectDescription,
    availablePhases,
    availableTasks,
    formErrors,
  } = samplingEventState;

  const handleProjectSelect = (selected) => {
    samplingEventUtils.handleProjectSelect(selected);
  };

  const handlePhaseSelect = (selected) => {
    samplingEventUtils.handlePhaseSelect(selected);
  };

  const handleTaskSelect = (selected) => {
    samplingEventUtils.handleTaskSelect(selected);
  };

  return (
    <>
      <div className={styles.projectInfoSection}>
        <div className={styles.formField}>
          <label htmlFor="project">Project</label>
          <Select
            id="project"
            value={selectedProject}
            onChange={handleProjectSelect}
            options={allProjectInfo}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder={
              formErrors?.project ? formErrors.project : "Select project"
            }
            styles={samplingEventSelect(formErrors?.project)}
            className={formErrors?.project ? styles.error : ""}
          />
        </div>

        <div className={styles.formField}>
          <label htmlFor="phase">Phase</label>
          <Select
            id="phase"
            value={selectedPhase}
            onChange={handlePhaseSelect}
            options={availablePhases}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder={
              selectedProject
                ? formErrors?.phase
                  ? formErrors.phase
                  : "Select phase"
                : "Select a project first"
            }
            styles={samplingEventSelect(formErrors?.phase)}
            isDisabled={!selectedProject}
          />
        </div>

        <div className={styles.formField}>
          <label htmlFor="task">Task</label>
          <Select
            id="task"
            value={selectedTask}
            onChange={handleTaskSelect}
            options={availableTasks}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder={
              selectedPhase
                ? formErrors?.task
                  ? formErrors.task
                  : availableTasks.length === 0
                  ? "No tasks available for this phase"
                  : "Select task"
                : "Select a phase first"
            }
            styles={samplingEventSelect(formErrors?.task)}
            isDisabled={!selectedPhase || availableTasks.length === 0}
          />
        </div>
      </div>
    </>
  );
};

// CreateSamplingEventForm component
export const CreateSamplingEventForm = ({
  setShowCreateSamplingEventForm,
  tableName,
}) => {
  // Projects
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  // Employees
  const allEmployees = useAppStore((s) => s.employees.list, shallow);
  // Loading
  const { showLoading, hideLoading } = useAppStore(
    (s) => s.loadingActions,
    shallow
  );
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );
  const cse = useAppStore((s) => s.createSamplingEvent, shallow);
  const cseActions = useAppStore((s) => s.createSamplingEventActions, shallow);
  const ui = useAppStore((s) => s.createSamplingEventUI, shallow);
  const uiActions = useAppStore((s) => s.createSamplingEventUIActions, shallow);
  const samplingSites = useAppStore((s) => s.samplingSites.sites, shallow);
  const user = useUserState().currentUser;

  // Updating the sampling events
  const fetchSamplingEventsPage = useAppStore(
    (s) => s.samplingEventsListActions.fetchSamplingEventsPage,
    shallow
  );

  // Adapter to keep existing subcomponents untouched
  const samplingEventState = {
    // Site & wells
    samplingSites,
    newSelectedSamplingSite: cse.newSelectedSamplingSite,
    selectedSiteWellInfo: cse.selectedSiteWellInfo,
    selectedWellIds: cse.selectedWellIds,
    setNewSelectedSamplingSite: cseActions.setNewSelectedSamplingSite,
    setSelectedSiteWellInfo: cseActions.setSelectedSiteWellInfo,
    setSelectedWellIds: cseActions.setSelectedWellIds,
    // Wells UI
    currentWellPage: ui.currentWellPage,
    setCurrentWellPage: uiActions.setCurrentWellPage,
    wellsPerPage: ui.wellsPerPage,
    setWellsPerPage: uiActions.setWellsPerPage,
    wellFilters: ui.wellFilters,
    setWellFilters: uiActions.setWellFilters,
    wellSort: ui.wellSort,
    setWellSort: uiActions.setWellSort,
    isFilterMenuOpen: ui.isFilterMenuOpen,
    setIsFilterMenuOpen: (v) => uiActions.toggleFilterMenu(v),
    // Groups
    samplingGroups: cse.samplingGroups,
    setSamplingGroups: cseActions.setSamplingGroups,
    nextGroupId: cse.nextGroupId,
    setNextGroupId: cseActions.setNextGroupId,
    // Event info
    eventName: cse.eventName,
    setEventName: cseActions.setEventName,
    startDate: cse.startDate,
    setStartDate: cseActions.setStartDate,
    endDate: cse.endDate,
    setEndDate: cseActions.setEndDate,
    teamLead: cse.teamLead,
    setTeamLead: cseActions.setTeamLead,
    // Project info
    noProjectNumberChecked: cse.noProjectNumberChecked,
    setNoProjectNumberChecked: cseActions.setNoProjectNumberChecked,
    selectedProject: cse.selectedProject,
    selectedPhase: cse.selectedPhase,
    selectedTask: cse.selectedTask,
    availablePhases: cse.availablePhases,
    availableTasks: cse.availableTasks,
    // Errors
    formErrors: cse.formErrors,
  };

  const samplingEventUtils = {
    handleDateChange: (date, type) =>
      type === "start"
        ? cseActions.setStartDate(date)
        : cseActions.setEndDate(date),
    handleTeamLeadSelect: (selected) => cseActions.setTeamLead(selected),
    handleProjectSelect: (project) => cseActions.setSelectedProject(project),
    handlePhaseSelect: (phase) => cseActions.setSelectedPhase(phase),
    handleTaskSelect: (task) => cseActions.setSelectedTask(task),
  };
  const handleSiteNameChange = async (selectedOption) => {
    showLoading();
    try {
      cseActions.setNewSelectedSamplingSite(selectedOption);
      await cseActions.fetchSamplingWellSiteInfo(selectedOption.value);
    } finally {
      hideLoading();
    }
  };

  const handleSubmit = async () => {
    const ok = cseActions.validateSamplingEventForm();
    if (!ok) {
      const errors = useAppStore.getState().createSamplingEvent.formErrors;
      const errorMessages = Object.values(errors).join(", ");
      console.log(errors);
      showNotification({
        type: "error",
        title: "Form Validation Errors",
        message: errorMessages,
      });
      return;
    }

    showLoading();
    try {
      const res = await cseActions.createSamplingEvent(user);
      if (res?.ok) {
        showNotification({
          type: "success",
          title: "Sampling event created successfully",
        });
        setShowCreateSamplingEventForm();
        cseActions.resetCreateSamplingEventForm();
        fetchSamplingEventsPage();
      } else {
        showNotification({
          type: "error",
          title: "Error creating sampling event",
        });
      }
    } finally {
      hideLoading();
    }
  };

  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All unsaved changes will be lost."
      )
    ) {
      setShowCreateSamplingEventForm();
      cseActions.resetCreateSamplingEventForm();
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h1>Create Sampling Event</h1>
      </div>
      <div className={styles.formBody}>
        <div className={styles.formSection}>
          <h2>Event Information</h2>
          <EventInformationSection
            samplingEventState={samplingEventState}
            allEmployees={allEmployees}
            samplingEventUtils={samplingEventUtils}
          />
        </div>

        <div className={styles.formSection}>
          <h2>Project Information</h2>
          <ProjectInformationSection
            samplingEventState={samplingEventState}
            allProjectInfo={allProjectInfo}
            samplingEventUtils={samplingEventUtils}
          />
        </div>

        <div className={styles.formSection}>
          <h2>Sampling Site</h2>
          <SamplingSiteSelect
            samplingEventState={samplingEventState}
            handleSiteNameChange={handleSiteNameChange}
          />
        </div>

        <div className={styles.formSection}>
          <h2>Group Creation</h2>
          <GroupCreation
            samplingEventState={samplingEventState}
            allEmployees={allEmployees}
            selectedSiteWellInfo={samplingEventState.selectedSiteWellInfo}
          />
        </div>

        <div className={styles.formSection}>
          <h2>Wells</h2>
          <WellsList samplingEventState={samplingEventState} />
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.submitButton}
            onClick={handleSubmit}
          >
            Create Event
          </button>
        </div>
      </div>
    </div>
  );
};
